import { inputClass, secondaryBtnClass } from "@/components/common/surfaceClasses";
import type { Dispatch, SetStateAction } from "react";
import { addTrainingThreadFeedback, escalateTrainingThreadToDev } from "@/lib/training/trainerThreadingClient";
import { isThreadNotFoundError } from "./trainingPanelGuidance";

type ThreadActionBusy = "note" | "escalate" | null;
type ThreadActionMessage = { tone: "ok" | "err"; text: string } | null;

type TrainingAskThreadToolsProps = {
  canEscalateKbThread: boolean;
  askThreadId: string | null;
  threadToolsOpen: boolean;
  setThreadToolsOpen: Dispatch<SetStateAction<boolean>>;
  threadFeedback: string;
  setThreadFeedback: Dispatch<SetStateAction<string>>;
  threadActionMessage: ThreadActionMessage;
  setThreadActionMessage: Dispatch<SetStateAction<ThreadActionMessage>>;
  threadActionBusy: ThreadActionBusy;
  setThreadActionBusy: Dispatch<SetStateAction<ThreadActionBusy>>;
  scopedOrganizationId: string;
};

export function TrainingAskThreadTools({
  canEscalateKbThread,
  askThreadId,
  threadToolsOpen,
  setThreadToolsOpen,
  threadFeedback,
  setThreadFeedback,
  threadActionMessage,
  setThreadActionMessage,
  threadActionBusy,
  setThreadActionBusy,
  scopedOrganizationId,
}: TrainingAskThreadToolsProps) {

  if (!canEscalateKbThread || !askThreadId) return null;

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-800/70 dark:bg-amber-950/30">
      <button type="button" className="w-full flex items-center justify-between text-left" onClick={() => setThreadToolsOpen((v) => !v)}>
        <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">Trainer thread notes & escalation</span>
        <span className="text-xs text-amber-800 dark:text-amber-300">{threadToolsOpen ? "Hide" : "Show"}</span>
      </button>
      {threadToolsOpen ? (
        <>
          <p className="mt-1 text-xs text-gray-700 dark:text-zinc-300">Use this when KB coverage is adequate but the API behavior/response is wrong. It will attach this full thread to a Dev bug report.</p>
          <textarea className={`mt-2 w-full ${inputClass} border-amber-200 dark:border-amber-800/60 min-h-[88px]`} value={threadFeedback} onChange={(e) => { setThreadFeedback(e.target.value); setThreadActionMessage(null); }} placeholder="Trainer notes: what should have happened, what failed, and why coverage appears adequate..." />
          {threadActionMessage ? <div className={threadActionMessage.tone === "ok" ? "mt-2 text-xs text-emerald-700" : "mt-2 text-xs text-red-700"}>{threadActionMessage.text}</div> : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!threadFeedback.trim() || threadActionBusy !== null}
              className={`${secondaryBtnClass} px-2.5 py-1.5 text-xs font-semibold disabled:opacity-50`}
              onClick={async () => {
                if (!askThreadId) return;
                setThreadActionBusy("note");
                setThreadActionMessage(null);
                try {
                  await addTrainingThreadFeedback({ organizationId: scopedOrganizationId, threadId: askThreadId, feedbackText: threadFeedback.trim(), feedbackKind: "trainer_note" });
                  setThreadActionMessage({ tone: "ok", text: "Trainer note saved to thread." });
                } catch (noteErr) {
                  if (isThreadNotFoundError(noteErr)) {
                    setThreadActionMessage({ tone: "ok", text: "Thread context expired. Continue using non-threaded KB feedback for this issue." });
                    return;
                  }
                  setThreadActionMessage({ tone: "err", text: noteErr instanceof Error ? noteErr.message : "Failed to save trainer note." });
                } finally {
                  setThreadActionBusy(null);
                }
              }}
            >
              {threadActionBusy === "note" ? "Saving…" : "Save note"}
            </button>
            <button
              type="button"
              disabled={!threadFeedback.trim() || threadActionBusy !== null}
              className="rounded-lg bg-amber-600 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              onClick={async () => {
                if (!askThreadId) return;
                setThreadActionBusy("escalate");
                setThreadActionMessage(null);
                try {
                  const out = await escalateTrainingThreadToDev({ organizationId: scopedOrganizationId, threadId: askThreadId, trainerFeedback: threadFeedback.trim() });
                  setThreadActionMessage({ tone: "ok", text: `Escalated to Dev as bug ${out.bugId}.` });
                } catch (escErr) {
                  if (isThreadNotFoundError(escErr)) {
                    setThreadActionMessage({ tone: "err", text: "Thread not found. Please switch to non-threaded mode and escalate from the latest response." });
                    return;
                  }
                  setThreadActionMessage({ tone: "err", text: escErr instanceof Error ? escErr.message : "Failed to escalate thread." });
                } finally {
                  setThreadActionBusy(null);
                }
              }}
            >
              {threadActionBusy === "escalate" ? "Escalating…" : "Escalate to Dev"}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
